import { memo, FC, useMemo } from 'react';

import { keyBy, has, orderBy } from 'lodash';

import {
  ConnectionTargetTypeCode,
  Cooperative,
  MassMessageRecipient,
  MassMessageSpace,
  MassMessageEntityWithRecipients,
} from 'models';

import { useCurrentCooperative, useDebounce } from 'hooks';

import {
  communicationMessagesApi,
  RecipientsBody,
} from 'services/communication-messages';

import getUniqueConnections from 'utils/getUniqueConnections';

import { RecepientsDetails } from 'components';

function getUniqueRecipients(recipients: MassMessageRecipient[]) {
  let uniqueRecipientsDict: Record<string, MassMessageRecipient> = {};

  for (let recipient of recipients) {
    if (!has(uniqueRecipientsDict, recipient.Id)) {
      uniqueRecipientsDict[recipient.Id] = recipient;
    }
  }

  return Object.values(uniqueRecipientsDict);
}

function transformMassMessageRecipients(
  recipients: MassMessageRecipient[],
  spaces: MassMessageSpace[],
  cooperative: Cooperative | null
) {
  const spacesDict = keyBy(spaces, 'Id');
  const spacesWithRecipientsDict: Record<
    string,
    MassMessageEntityWithRecipients
  > = {};
  const cooperativeWithRecipientsDict: MassMessageEntityWithRecipients = {
    Id: cooperative?.Id ?? '',
    Name: cooperative?.Name ?? '',
    Recipients: [],
    Type: ConnectionTargetTypeCode.Cooperative,
    Order: null,
  };

  for (let recipient of recipients) {
    const uniqueConnections = getUniqueConnections(recipient.Connections);
    for (let connection of uniqueConnections) {
      if (connection.TargetId) {
        if (connection.TargetType === ConnectionTargetTypeCode.Space) {
          const space = spacesDict[connection.TargetId];
          if (!space) continue;

          if (has(spacesWithRecipientsDict, space.Id)) {
            spacesWithRecipientsDict[space.Id].Recipients.push(recipient);
          } else {
            spacesWithRecipientsDict[space.Id] = {
              Id: space.Id,
              Name: space.Name,
              Recipients: [recipient],
              Type: ConnectionTargetTypeCode.Space,
              Order: space.Order,
            };
          }
        }

        if (connection.TargetType === ConnectionTargetTypeCode.Cooperative) {
          cooperativeWithRecipientsDict.Recipients.push(recipient);
        }
      }
    }
  }

  cooperativeWithRecipientsDict.Recipients = getUniqueRecipients(
    cooperativeWithRecipientsDict.Recipients
  );
  let spacesWithRecipients = orderBy(
    Object.values(spacesWithRecipientsDict).map((space) => ({
      ...space,
      Recipients: getUniqueRecipients(space.Recipients),
    })),
    (space) => space.Order,
    'asc'
  );

  return [cooperativeWithRecipientsDict, ...spacesWithRecipients];
}

interface Props {
  recipientsBody: RecipientsBody;
}

const Recipients: FC<Props> = ({ recipientsBody }) => {
  const cooperative = useCurrentCooperative();
  const debouncedBody = useDebounce(recipientsBody, 500);

  const {
    isError,
    isLoading,
    isFetching,
    data: baseRecipients,
  } = communicationMessagesApi.useGetRecipientsListQuery(
    {
      recipientsBody: debouncedBody,
    },
    {
      skip: !!!debouncedBody.coopId,
      refetchOnMountOrArgChange: true,
    }
  );

  const recipientsEntities = useMemo(
    () =>
      baseRecipients
        ? transformMassMessageRecipients(
            baseRecipients,
            recipientsBody.selectedSpaces,
            cooperative
          )
        : [],
    [baseRecipients, recipientsBody.selectedSpaces, cooperative]
  );

  return (
    <RecepientsDetails
      error={isError}
      loading={isLoading || isFetching}
      recipientsEntities={recipientsEntities}
    />
  );
};

export default memo(Recipients);
